import { BlackFridayOfferDTO, ProductAccountDTO } from 'api-models'
import axios from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import {
	BlackFridayOffers,
	blackFridayOfferQuery
} from 'services/user/black-friday-offer'
import { ProductAccounts } from 'services/user'
import { useAccountStore, useUserStore } from 'store'
import { parseError } from 'utils/errors'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent } from 'utils/posthog-events'

const CAMPAIGN = 'black-friday-presale-2026'
const CHANNEL = 'clickguard-black-friday-offer'
const confirmed = (offer?: BlackFridayOfferDTO) =>
	offer?.claim?.status === 'claimed' || offer?.claim?.status === 'switched'
const storageKey = (accountId: string, suffix: string) =>
	`${CAMPAIGN}:${accountId}:${suffix}`
const readSession = (key: string) => {
	try {
		return sessionStorage.getItem(key) === '1'
	} catch {
		return false
	}
}
const writeSession = (key: string, value: boolean) => {
	try {
		if (value) sessionStorage.setItem(key, '1')
		else sessionStorage.removeItem(key)
	} catch {
		/* Storage can be unavailable in private browsing. */
	}
}

export const useBlackFridayOffer = (accountId: string, userId: string) => {
	const queryClient = useQueryClient()
	const trackEvent = useTrackPostHogEvent()
	const displayed = useRef(false)
	const key = ['black-friday-offer', accountId, userId]
	const mounted = useRef(true)
	const submitting = useRef(false)
	const channel = useRef<BroadcastChannel | null>(null)
	const pendingRef = useRef(readSession(storageKey(accountId, 'pending')))
	const refreshedConfirmation = useRef(false)
	const [dismissed, setDismissed] = useState(() =>
		readSession(storageKey(accountId, 'dismissed'))
	)
	const [modalOpen, setModalOpen] = useState(false)
	const [reviewedOffer, setReviewedOffer] =
		useState<BlackFridayOfferDTO | null>(null)
	const [phase, setPhase] = useState<'idle' | 'submitting' | 'pending'>(
		pendingRef.current ? 'pending' : 'idle'
	)
	const [error, setError] = useState<string | null>(null)
	const [expired, setExpired] = useState(false)
	const isCurrent = useCallback(
		() =>
			mounted.current &&
			useAccountStore.getState().account?.id === accountId &&
			useUserStore.getState().user?.id === userId,
		[accountId, userId]
	)
	const track = useCallback(
		(event: PostHogEvent, parameters?: Record<string, string | number>) => {
			if (!isCurrent()) return
			try {
				trackEvent(event, 'black_friday_offer', {
					campaign_id: CAMPAIGN,
					account_id: accountId,
					...parameters
				})
			} catch {
				/* Analytics must not interrupt billing. */
			}
		},
		[trackEvent, isCurrent, accountId]
	)

	const query = useQuery(
		key,
		({ signal }) => blackFridayOfferQuery(accountId, signal),
		{
			enabled: !!accountId && !!userId,
			staleTime: 15000,
			retry: 1,
			refetchOnWindowFocus: 'always',
			refetchInterval: (data) =>
				confirmed(data?.offer)
					? false
					: data &&
					  Date.parse(data.offer.server_now) >=
							Date.parse(data.offer.claim_deadline) &&
					  data.offer.claim?.status !== 'claiming' &&
					  !pendingRef.current
					? false
					: 60000,
			refetchIntervalInBackground: false
		}
	)
	const offer = query.data?.offer
	const pending = phase !== 'idle' || offer?.claim?.status === 'claiming'

	const setPending = useCallback(
		(value: boolean) => {
			pendingRef.current = value
			writeSession(storageKey(accountId, 'pending'), value)
			setPhase(value ? 'pending' : 'idle')
		},
		[accountId]
	)

	const refreshWorkspace = useCallback(
		async (account?: ProductAccountDTO) => {
			if (!isCurrent()) return
			if (account?.id === accountId) useAccountStore.setState({ account })
			const [accountResult, subscriptionResult] = await Promise.allSettled([
				ProductAccounts.getById(accountId),
				ProductAccounts.getSubscriptionByAccountId(accountId)
			])
			if (!isCurrent()) return
			useAccountStore.setState((state) => ({
				...(accountResult.status === 'fulfilled'
					? {
							account: accountResult.value,
							accounts: state.accounts.map((item) =>
								item.id === accountId ? accountResult.value : item
							)
					  }
					: {}),
				...(subscriptionResult.status === 'fulfilled'
					? {
							subscription: subscriptionResult.value,
							subscriptionLoading: false
					  }
					: {})
			}))
		},
		[accountId, isCurrent]
	)

	const finishClaim = useCallback(
		(account?: ProductAccountDTO) => {
			if (!isCurrent()) return
			void queryClient.invalidateQueries([
				'protection-context',
				accountId,
				userId
			])
			setPending(false)
			setModalOpen(false)
			setError(null)
			if (!refreshedConfirmation.current) {
				refreshedConfirmation.current = true
				void refreshWorkspace(account)
			}
		},
		[isCurrent, refreshWorkspace, setPending, queryClient, accountId, userId]
	)

	useEffect(() => {
		mounted.current = true
		return () => {
			mounted.current = false
		}
	}, [])

	useEffect(() => {
		if (!query.data) return
		let timer: ReturnType<typeof setTimeout>
		const checkDeadline = () => {
			const serverNow =
				Date.parse(query.data!.offer.server_now) +
				performance.now() -
				query.data!.receivedAt
			const remaining = Date.parse(query.data!.offer.claim_deadline) - serverNow
			setExpired(!Number.isFinite(remaining) || remaining <= 0)
			if (remaining > 0)
				timer = setTimeout(checkDeadline, Math.min(remaining + 1, 86400000))
		}
		checkDeadline()
		return () => clearTimeout(timer)
	}, [query.data])

	useEffect(() => {
		if (!offer || submitting.current || !isCurrent()) return
		if (confirmed(offer)) {
			if (pendingRef.current) toast.success('Black Friday offer claimed')
			finishClaim()
		} else if (offer.claim?.status === 'claiming') {
			setPending(true)
		} else if (
			offer.claim?.status === 'failed' ||
			offer.claim?.status === 'cancelled'
		) {
			setPending(false)
			setError(
				'The offer could not be confirmed. Refresh the offer before trying again.'
			)
		}
	}, [offer, finishClaim, isCurrent, setPending])

	useEffect(() => {
		const refresh = () => {
			if (isCurrent())
				void queryClient.invalidateQueries([
					'black-friday-offer',
					accountId,
					userId
				])
		}
		const onMessage = (event: MessageEvent) => {
			if (event.data?.accountId === accountId) refresh()
		}
		try {
			channel.current = new BroadcastChannel(CHANNEL)
			channel.current.addEventListener('message', onMessage)
		} catch {
			/* Focus refresh and polling cover unsupported browsers. */
		}
		const onUpdate = (event: Event) => {
			if ((event as CustomEvent).detail?.accountId === accountId) refresh()
		}
		window.addEventListener(CHANNEL, onUpdate)
		return () => {
			window.removeEventListener(CHANNEL, onUpdate)
			channel.current?.close()
			channel.current = null
		}
	}, [accountId, userId, isCurrent, queryClient])

	const openModal = () => {
		track(PostHogEvent.BLACK_FRIDAY_MODAL_OPENED)
		setReviewedOffer(offer || null)
		setError(null)
		setModalOpen(true)
		void query.refetch()
	}
	const closeModal = useCallback(() => setModalOpen(false), [])
	const refresh = async () => {
		const result = await query.refetch()
		if (!isCurrent()) return
		if (result.data && !confirmed(result.data.offer))
			setReviewedOffer(result.data.offer)
		setError(
			result.error ? 'Unable to refresh the offer. Please try again.' : null
		)
	}
	// Explicit retries use the server's unique workspace reservation; an empty GET never clears pending.
	const claim = async (recoverPending = false) => {
		const account = useAccountStore.getState().account
		const access = account?.user_access.find(
			(item) => item.user_id === userId
		)?.access_level
		if (
			!isCurrent() ||
			submitting.current ||
			(pending && !recoverPending) ||
			(recoverPending && (!pendingRef.current || !!offer?.claim)) ||
			!['OWNER', 'WRITE'].includes(access || '') ||
			!reviewedOffer?.quote_version ||
			expired
		)
			return
		submitting.current = true
		setPhase('submitting')
		setError(null)
		let posted = false
		try {
			await queryClient.cancelQueries(key)
			const fresh = await BlackFridayOffers.get(accountId)
			if (!isCurrent()) return
			queryClient.setQueryData(key, {
				offer: fresh,
				receivedAt: performance.now()
			})
			if (confirmed(fresh)) {
				finishClaim()
				toast.success('This workspace has already claimed the offer')
				return
			}
			if (fresh.claim?.status === 'claiming') {
				setPending(true)
				return
			}
			if (recoverPending && fresh.claim) {
				setPending(false)
				setReviewedOffer(fresh)
				setError('Review the refreshed offer before claiming again.')
				return
			}
			if (
				!fresh.can_claim ||
				!fresh.eligible ||
				Date.parse(fresh.server_now) >= Date.parse(fresh.claim_deadline)
			) {
				setReviewedOffer(fresh)
				setError(
					fresh.claim_blocked_reason ||
						'This offer is no longer available for this workspace.'
				)
				return
			}
			if (fresh.quote_version !== reviewedOffer.quote_version) {
				setReviewedOffer(fresh)
				setError(
					'The offer has changed. Review the updated dates before claiming again.'
				)
				return
			}
			posted = true
			writeSession(storageKey(accountId, 'pending'), true)
			const result = await BlackFridayOffers.claim(
				accountId,
				fresh.quote_version!
			)
			if (!isCurrent()) return
			if (result.kind === 'processing') {
				setPending(true)
				return
			}
			if (result.kind !== 'claimed' && result.kind !== 'already_claimed') {
				throw new Error('Unconfirmed claim response')
			}
			await queryClient.cancelQueries(key)
			if (!isCurrent()) return
			queryClient.setQueryData(key, {
				offer: {
					...fresh,
					eligible: false,
					reason: 'offer_claimed',
					claim: result.claim,
					quote: result.quote
				},
				receivedAt: performance.now()
			})
			finishClaim(result.account)
			toast.success(
				result.kind === 'already_claimed'
					? 'This workspace has already claimed the offer'
					: 'Black Friday offer claimed'
			)
			try {
				channel.current?.postMessage({ accountId })
			} catch {
				/* Polling remains available. */
			}
			window.dispatchEvent(new CustomEvent(CHANNEL, { detail: { accountId } }))
		} catch (err: unknown) {
			if (!isCurrent()) return
			const status = axios.isAxiosError(err) ? err.response?.status : undefined
			if (posted && (!status || status === 408 || status >= 500)) {
				setPending(true)
				setError(
					'We are checking your claim status. Please do not submit another claim. You can check again below.'
				)
			} else {
				if (posted && status)
					track(PostHogEvent.BLACK_FRIDAY_CLAIM_FAILED, { http_status: status })
				if (!recoverPending)
					writeSession(storageKey(accountId, 'pending'), false)
				setError(parseError(err))
			}
			void query.refetch()
		} finally {
			submitting.current = false
			if (isCurrent() && !pendingRef.current) setPhase('idle')
		}
	}
	const windowClosed =
		expired ||
		(!!query.data &&
			Date.parse(offer!.server_now) +
				performance.now() -
				query.data.receivedAt >=
				Date.parse(offer!.claim_deadline))
	const visible =
		!!offer &&
		!query.isError &&
		!dismissed &&
		!windowClosed &&
		!confirmed(offer) &&
		(offer.eligible || offer.claim?.status === 'claiming')
	useEffect(() => {
		if (visible && !displayed.current) {
			displayed.current = true
			track(PostHogEvent.BLACK_FRIDAY_BANNER_DISPLAYED)
		}
	}, [visible, track])

	return {
		offer,
		reviewedOffer,
		modalOpen,
		closeModal,
		openModal,
		claim: () => claim(),
		retryPendingClaim: () => claim(true),
		canRetryPending:
			pendingRef.current &&
			!offer?.claim &&
			!!offer?.eligible &&
			offer.can_claim === true &&
			!query.isError,
		refresh,
		error:
			error ||
			(modalOpen && query.isError
				? 'Unable to refresh the offer. Please try again.'
				: null),
		pending,
		submitting: phase === 'submitting',
		refreshing: query.isFetching,
		expired: windowClosed,
		visible,
		dismiss: () => {
			track(PostHogEvent.BLACK_FRIDAY_BANNER_DISMISSED)
			writeSession(storageKey(accountId, 'dismissed'), true)
			setDismissed(true)
		}
	}
}
